import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import JobService from "../../service/job";

const statusList = [
  { value: -1, label: "All" },
  { value: "progressing", label: "Progressing" },
  { value: "production", label: "Production" },
  { value: "freight", label: "Freight" },
  { value: "delay", label: "Delay" },
  { value: "completed", label: "Completed" },
  // { value: 1, label: "Awaiting Assign" },
  // { value: 2, label: "Awaiting Order" },
  // { value: 3, label: "Awaiting Order Approval" },
  // { value: 4, label: "Artwork in process" },
  // { value: 5, label: "Awaiting Artwork Approval" },
  // { value: 6, label: "Image File in process" },
  // { value: 7, label: "Awaiting Image File Approval" },
  // { value: 8, label: "In Production" },
  // { value: 9, label: "Delivery Underway" },
  // { value: 10, label: "Completed" },
];

const JobsFilter = ({ onFilterChange, onSearch, onReset, initialFilters, classes, hideStatusFilter, excludeStatus, isSupplierDashboard }) => {
  const { user } = useSelector((state) => state);
  const role = user.currentUser?.Role_id;

  const defaultEmployee =
    user.currentUser.Role_id === 1 ? -1 : user.currentUser.isAdminOrEmp === 1 ? user.currentUser.User_id : -1;
  const defaultCustomer =
    user.currentUser.isAdminOrEmp === 1 || user.currentUser.Role_id === 4 ? -1 : user.currentUser.User_id;
  const defaultSupplier = user.currentUser.Role_id === 4 ? user.currentUser.User_id : -1;

  const [Job_id, setJob_id] = useState(initialFilters?.Job_id || "");
  const [status, setStatus] = useState(initialFilters?.status ?? -1);
  const [customer, setCustomer] = useState(initialFilters?.customer ?? defaultCustomer);
  const [startdate, setStartdate] = useState(
    initialFilters?.startdate || new Date(new Date().setFullYear(new Date().getFullYear() - 1))
  );
  const [employee, setEmployee] = useState(initialFilters?.employee ?? defaultEmployee);
  const [supplier, setSupplier] = useState(initialFilters?.supplier ?? defaultSupplier);
  const searchRef = useRef(null);

  const [filters, setFilters] = useState({
    customers: [{ label: "All", value: -1 }],
    employees: [{ label: "All", value: -1 }],
    suppliers: [{ label: "All", value: -1 }],
    status: statusList,
  });

  useEffect(() => {
    (async () => {
      const jobService = new JobService();
      const response = await jobService.getJobFilters();

      if (response.success) {
        const { customers, employees, suppliers } = response.data;

        setFilters((prev) => ({
          ...prev,
          customers: [...prev.customers, ...customers],
          employees: [...prev.employees, ...employees],
          suppliers: [...prev.suppliers, ...suppliers],
        }));
      }
    })();
  }, []);

  const handleFilter = (key, value) => {
    const newFilters = {
      status,
      customer,
      employee,
      startdate,
      supplier,
      [key]: value,
    };

    switch (key) {
      case "status":
        setStatus(value);
        break;
      case "customer":
        setCustomer(value);
        break;
      case "employee":
        setEmployee(value);
        break;
      case "startdate":
        setStartdate(value);
        break;
      case "supplier":
        setSupplier(value);
        break;
      default:
        break;
    }

    onFilterChange(newFilters);
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setJob_id(searchRef.current.value);
      onSearch({ Job_id: searchRef.current.value });
    }
  };

  const handleClearSearch = () => {
    searchRef.current.value = "";
    setJob_id("");
    onSearch({ Job_id: "" });
  };

  const resetFilters = () => {
    searchRef.current.value = "";
    setJob_id("");
    setStatus(-1);
    setEmployee(defaultEmployee);
    setCustomer(defaultCustomer);
    setStartdate(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
    setSupplier(defaultSupplier);
    onReset();
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
      <TextField
        inputRef={searchRef}
        onKeyPress={handleSearch}
        placeholder="Job id"
        variant="outlined"
        size="small"
        sx={{ width: "130px" }}
        defaultValue={Job_id}
        InputProps={{
          startAdornment: <SearchIcon color="primary" />,
          ...(Job_id && {
            endAdornment: (
              <IconButton onClick={handleClearSearch} size="small">
                <ClearIcon color="primary" />
              </IconButton>
            ),
          }),
        }}
      />

      {defaultCustomer === -1 && role !== 4 && !isSupplierDashboard && (
        <TextField
          label="Customer"
          variant="outlined"
          size="small"
          value={customer}
          onChange={(e) => handleFilter("customer", e.target.value)}
          select
          sx={{ width: "150px" }}
          className={classes.StatusDrp}
        >
          {filters.customers.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}

      {(role === 1 || role === 2) && !!isSupplierDashboard && (
        <TextField
          label="Supplier"
          variant="outlined"
          size="small"
          value={supplier}
          onChange={(e) => handleFilter("supplier", e.target.value)}
          select
          sx={{ width: "150px" }}
          className={classes.StatusDrp}
        >
          {filters.suppliers.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="Start Date"
          value={startdate}
          onChange={(startdate) => handleFilter("startdate", startdate)}
          inputFormat="DD/MM/YYYY"
          renderInput={(params) => <TextField sx={{ width: "175px" }} variant="outlined" size="small" {...params} />}
        />
      </LocalizationProvider>

      {user.currentUser?.Role_id === 1 && (
        <TextField
          label="Employee"
          variant="outlined"
          size="small"
          value={employee}
          onChange={(e) => handleFilter("employee", e.target.value)}
          select
          sx={{ width: "150px" }}
          className={classes.StatusDrp}
        >
          {filters.employees.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}

      {!hideStatusFilter && (
        <TextField
          label="Status"
          variant="outlined"
          size="small"
          value={status}
          onChange={(e) => handleFilter("status", e.target.value)}
          select
          sx={{ width: "150px" }}
          className={classes.StatusDrp}
        >
          {statusList
            .filter((option) => !excludeStatus.includes(option.value))
            .map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
        </TextField>
      )}

      <Box sx={{ flexGrow: 1 }} />
      <IconButton onClick={resetFilters} className="btn btn-icon btn-dark">
        <RefreshIcon />
      </IconButton>
    </Stack>
  );
};

JobsFilter.propTypes = {
  onFilterChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  initialFilters: PropTypes.object,
  classes: PropTypes.object,
  hideStatusFilter: PropTypes.bool,
  excludeStatus: PropTypes.array,
  isSupplierDashboard: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
};

JobsFilter.defaultProps = {
  initialFilters: {},
  classes: {},
  classes: {},
  hideStatusFilter: false,
  excludeStatus: [],
};

export default JobsFilter;
